/**
 * API Provider
 *
 * Provides authenticated API functions using App Bridge session tokens.
 * Wraps raw API calls with token management and response unwrapping.
 */

import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';
import { useAppBridge } from './AppBridgeProvider';
import type { Product, Shop, OptimizationJob, DashboardStats, BillingPlan } from '../types';
import {
  mockProducts,
  mockShop,
  mockDashboardStats,
  mockBillingPlans,
  createMockJob,
  getMockJob,
} from '../lib/mock-data';

const API_BASE = '/api';

/**
 * Enable mock mode for development without backend
 */
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Simulate network delay for mock mode
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * API response wrapper type (backend format)
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface ApiContextValue {
  // Dashboard
  getDashboardStats: () => Promise<DashboardStats>;
  // Products
  getProducts: () => Promise<Product[]>;
  getProduct: (id: string) => Promise<Product>;
  syncProducts: () => Promise<{ synced: number }>;
  // Optimization
  startOptimization: (productIds: string[]) => Promise<OptimizationJob>;
  getOptimizationJob: (jobId: string) => Promise<OptimizationJob>;
  applyOptimizations: (productIds: string[]) => Promise<{ applied: number; failed: number }>;
  applyAllOptimizations: () => Promise<{ applied: number; failed: number }>;
  getOptimizationPreview: (productId: string) => Promise<Product>;
  // Shop
  getShopInfo: () => Promise<Shop>;
  // Billing
  getBillingPlans: () => Promise<BillingPlan[]>;
  subscribeToPlan: (planId: string) => Promise<{ confirmationUrl: string }>;
  getCurrentSubscription: () => Promise<{ plan: string; status: string } | null>;
}

const ApiContext = createContext<ApiContextValue | null>(null);

interface ApiProviderProps {
  children: ReactNode;
}

export function ApiProvider({ children }: ApiProviderProps) {
  const { getSessionToken } = useAppBridge();

  /**
   * Make authenticated API request
   */
  const request = useCallback(async <T,>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const token = await getSessionToken();

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    const json = await response.json() as ApiResponse<T>;

    if (!response.ok || !json.success) {
      throw new ApiError(
        json.error || `Request failed: ${response.status}`,
        response.status
      );
    }

    return json.data;
  }, [getSessionToken]);

  // === Dashboard ===
  const getDashboardStats = useCallback(async (): Promise<DashboardStats> => {
    if (USE_MOCK) {
      await delay(300);
      return mockDashboardStats;
    }
    const stats = await request<{
      total: number;
      pending: number;
      optimized: number;
      applied: number;
    }>('/products/stats');

    // Transform backend stats to frontend format
    return {
      totalProducts: stats.total,
      optimizedProducts: stats.optimized,
      appliedProducts: stats.applied,
      pendingProducts: stats.pending,
      usagePercent: Math.round(((stats.optimized + stats.applied) / stats.total) * 100) || 0,
      plan: 'starter', // TODO: Get from shop info
      productsLimit: 500,
    };
  }, [request]);

  // === Products ===
  const getProducts = useCallback(async (): Promise<Product[]> => {
    if (USE_MOCK) {
      await delay(400);
      return mockProducts;
    }
    const result = await request<{
      products: Array<{
        id: string;
        shopifyId: string;
        title: string;
        descriptionOriginal: string;
        descriptionOptimized: string | null;
        usecases: string | null;
        faq: string | null;
        specs: string | null;
        status: string;
        optimizedAt: string | null;
        appliedAt: string | null;
      }>;
      total: number;
    }>('/products');

    return result.products.map(p => ({
      id: p.id,
      shopifyId: p.shopifyId,
      title: p.title,
      descriptionOriginal: p.descriptionOriginal,
      descriptionOptimized: p.descriptionOptimized || undefined,
      usecases: p.usecases ? JSON.parse(p.usecases) : undefined,
      faq: p.faq ? JSON.parse(p.faq) : undefined,
      specs: p.specs ? JSON.parse(p.specs) : undefined,
      status: p.status as Product['status'],
      optimizedAt: p.optimizedAt || undefined,
      appliedAt: p.appliedAt || undefined,
    }));
  }, [request]);

  const getProduct = useCallback(async (id: string): Promise<Product> => {
    if (USE_MOCK) {
      await delay(200);
      const product = mockProducts.find(p => p.id === id);
      if (!product) throw new ApiError('Produkt nicht gefunden', 404);
      return product;
    }
    const p = await request<{
      id: string;
      shopifyId: string;
      title: string;
      descriptionOriginal: string;
      descriptionOptimized: string | null;
      usecases: string | null;
      faq: string | null;
      specs: string | null;
      status: string;
      optimizedAt: string | null;
      appliedAt: string | null;
    }>(`/products/${id}`);

    return {
      id: p.id,
      shopifyId: p.shopifyId,
      title: p.title,
      descriptionOriginal: p.descriptionOriginal,
      descriptionOptimized: p.descriptionOptimized || undefined,
      usecases: p.usecases ? JSON.parse(p.usecases) : undefined,
      faq: p.faq ? JSON.parse(p.faq) : undefined,
      specs: p.specs ? JSON.parse(p.specs) : undefined,
      status: p.status as Product['status'],
      optimizedAt: p.optimizedAt || undefined,
      appliedAt: p.appliedAt || undefined,
    };
  }, [request]);

  const syncProducts = useCallback(async (): Promise<{ synced: number }> => {
    if (USE_MOCK) {
      await delay(1500);
      return { synced: 3 };
    }
    return request<{ synced: number }>('/products/sync', { method: 'POST' });
  }, [request]);

  // === Optimization ===
  const startOptimization = useCallback(async (productIds: string[]): Promise<OptimizationJob> => {
    if (USE_MOCK) {
      await delay(500);
      return createMockJob(productIds);
    }
    const result = await request<{
      jobId: string;
      status: string;
      totalProducts: number;
      successCount: number;
      errorCount: number;
    }>('/optimize', {
      method: 'POST',
      body: JSON.stringify({ productIds }),
    });

    return {
      id: result.jobId,
      status: result.status as OptimizationJob['status'],
      totalProducts: result.totalProducts,
      processedProducts: result.successCount + result.errorCount,
      successCount: result.successCount,
      errorCount: result.errorCount,
      createdAt: new Date().toISOString(),
      completedAt: result.status === 'completed' ? new Date().toISOString() : undefined,
    };
  }, [request]);

  const getOptimizationJob = useCallback(async (jobId: string): Promise<OptimizationJob> => {
    if (USE_MOCK) {
      await delay(100);
      const job = getMockJob(jobId);
      if (!job) throw new ApiError('Job nicht gefunden', 404);
      return job;
    }
    const result = await request<{
      id: string;
      status: string;
      progress: number;
      totalProducts: number;
      completedProducts: number;
      error: string | null;
      startedAt: string | null;
      completedAt: string | null;
    }>(`/optimize/${jobId}`);

    return {
      id: result.id,
      status: result.status as OptimizationJob['status'],
      totalProducts: result.totalProducts,
      processedProducts: result.completedProducts,
      successCount: result.completedProducts,
      errorCount: 0,
      createdAt: result.startedAt || new Date().toISOString(),
      completedAt: result.completedAt || undefined,
    };
  }, [request]);

  const applyOptimizations = useCallback(async (productIds: string[]): Promise<{ applied: number; failed: number }> => {
    if (USE_MOCK) {
      await delay(2000);
      return { applied: productIds.length, failed: 0 };
    }
    // For specific products, we need to use apply-all and filter
    // Or create a new endpoint - for now use apply-all
    return request<{ applied: number; failed: number }>('/optimize/apply-all', {
      method: 'POST',
    });
  }, [request]);

  const applyAllOptimizations = useCallback(async (): Promise<{ applied: number; failed: number }> => {
    if (USE_MOCK) {
      await delay(2000);
      return { applied: 5, failed: 0 };
    }
    return request<{ applied: number; failed: number }>('/optimize/apply-all', {
      method: 'POST',
    });
  }, [request]);

  const getOptimizationPreview = useCallback(async (productId: string): Promise<Product> => {
    if (USE_MOCK) {
      await delay(200);
      const product = mockProducts.find(p => p.id === productId);
      if (!product) throw new ApiError('Produkt nicht gefunden', 404);
      return product;
    }
    const result = await request<{
      title: string;
      originalDescription: string;
      optimizedDescription: string | null;
      usecases: string[] | null;
      faq: Array<{ question: string; answer: string }> | null;
      status: string;
    }>(`/optimize/preview/${productId}`);

    return {
      id: productId,
      shopifyId: '',
      title: result.title,
      descriptionOriginal: result.originalDescription,
      descriptionOptimized: result.optimizedDescription || undefined,
      usecases: result.usecases || undefined,
      faq: result.faq || undefined,
      status: result.status as Product['status'],
    };
  }, [request]);

  // === Shop ===
  const getShopInfo = useCallback(async (): Promise<Shop> => {
    if (USE_MOCK) {
      await delay(300);
      return mockShop;
    }
    // TODO: Create /api/shop endpoint
    return request<Shop>('/shop');
  }, [request]);

  // === Billing ===
  const getBillingPlans = useCallback(async (): Promise<BillingPlan[]> => {
    if (USE_MOCK) {
      await delay(400);
      return mockBillingPlans;
    }
    return request<BillingPlan[]>('/billing/plans');
  }, [request]);

  const subscribeToPlan = useCallback(async (planId: string): Promise<{ confirmationUrl: string }> => {
    if (USE_MOCK) {
      await delay(800);
      alert(`Demo: Würde zu Shopify Billing für Plan "${planId}" weiterleiten`);
      return { confirmationUrl: '#demo' };
    }
    return request<{ confirmationUrl: string }>('/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    });
  }, [request]);

  const getCurrentSubscription = useCallback(async (): Promise<{ plan: string; status: string } | null> => {
    if (USE_MOCK) {
      await delay(200);
      return { plan: 'starter', status: 'active' };
    }
    return request<{ plan: string; status: string } | null>('/billing/subscription');
  }, [request]);

  const value = useMemo(
    () => ({
      getDashboardStats,
      getProducts,
      getProduct,
      syncProducts,
      startOptimization,
      getOptimizationJob,
      applyOptimizations,
      applyAllOptimizations,
      getOptimizationPreview,
      getShopInfo,
      getBillingPlans,
      subscribeToPlan,
      getCurrentSubscription,
    }),
    [
      getDashboardStats,
      getProducts,
      getProduct,
      syncProducts,
      startOptimization,
      getOptimizationJob,
      applyOptimizations,
      applyAllOptimizations,
      getOptimizationPreview,
      getShopInfo,
      getBillingPlans,
      subscribeToPlan,
      getCurrentSubscription,
    ]
  );

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
}

/**
 * Hook to access API functions
 */
export function useApi() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within ApiProvider');
  }
  return context;
}

export { ApiError };
